// =============================================================================
// BP-08-05: Vendor Portal Frontend - useVendorProducts Hook
// =============================================================================

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { vendorProductsApi, vendorCategoriesApi, diagnosesApi } from '@/features/vendor/api/vendorApi';
import type { 
  VendorProduct, 
  ProductFormData, 
  ProductListParams, 
  ProductListResponse,
  ProductStatus 
} from '@/features/vendor/types/vendorTypes';

// Query keys
export const vendorProductKeys = {
  all: ['vendor-products'] as const,
  lists: () => [...vendorProductKeys.all, 'list'] as const,
  list: (params: ProductListParams) => [...vendorProductKeys.lists(), params] as const,
  details: () => [...vendorProductKeys.all, 'detail'] as const,
  detail: (id: string) => [...vendorProductKeys.details(), id] as const,
  categories: () => [...vendorProductKeys.all, 'categories'] as const,
  diagnoses: () => [...vendorProductKeys.all, 'diagnoses'] as const,
};

/**
 * Hook for fetching vendor products list
 */
export function useVendorProducts(params: ProductListParams = {}) {
  return useQuery({
    queryKey: vendorProductKeys.list(params),
    queryFn: () => vendorProductsApi.list(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook for infinite scrolling product list
 */
export function useVendorProductsInfinite(params: Omit<ProductListParams, 'page'> = {}) {
  return useInfiniteQuery({
    queryKey: vendorProductKeys.list({ ...params, page: 'infinite' } as ProductListParams),
    queryFn: ({ pageParam = 1 }) => vendorProductsApi.list({ ...params, page: pageParam }),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });
}

/**
 * Hook for fetching single product
 */
export function useVendorProduct(productId: string | undefined) {
  return useQuery({
    queryKey: vendorProductKeys.detail(productId!),
    queryFn: () => vendorProductsApi.get(productId!),
    enabled: !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for creating a product
 */
export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProductFormData) => vendorProductsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vendorProductKeys.lists() });
    },
  });
}

/**
 * Hook for updating a product
 */
export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: Partial<ProductFormData> }) =>
      vendorProductsApi.update(productId, data),
    onSuccess: (updatedProduct) => {
      // Update the specific product in cache
      queryClient.setQueryData<VendorProduct>(
        vendorProductKeys.detail(updatedProduct.id),
        updatedProduct
      );
      // Invalidate lists to refresh
      queryClient.invalidateQueries({ queryKey: vendorProductKeys.lists() });
    },
  });
}

/**
 * Hook for deleting a product
 */
export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) => vendorProductsApi.delete(productId),
    onSuccess: (_, productId) => {
      queryClient.removeQueries({ queryKey: vendorProductKeys.detail(productId) });
      queryClient.invalidateQueries({ queryKey: vendorProductKeys.lists() });
    },
  });
}

/**
 * Hook for updating product status
 */
export function useUpdateProductStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, status }: { productId: string; status: ProductStatus }) =>
      vendorProductsApi.updateStatus(productId, status),
    onSuccess: (updatedProduct) => {
      queryClient.setQueryData<VendorProduct>(
        vendorProductKeys.detail(updatedProduct.id),
        updatedProduct
      );
      queryClient.invalidateQueries({ queryKey: vendorProductKeys.lists() });
    },
  });
}

/**
 * Hook for uploading product images
 */
export function useUploadProductImages() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, files }: { productId: string; files: File[] }) =>
      vendorProductsApi.uploadImages(productId, files),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: vendorProductKeys.detail(productId) });
    },
  });
}

/**
 * Hook for deleting a product image
 */
export function useDeleteProductImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, imageUrl }: { productId: string; imageUrl: string }) =>
      vendorProductsApi.deleteImage(productId, imageUrl),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({ queryKey: vendorProductKeys.detail(productId) });
    },
  });
}

/**
 * Hook for fetching vendor categories
 */
export function useVendorCategories() {
  return useQuery({
    queryKey: vendorProductKeys.categories(),
    queryFn: () => vendorCategoriesApi.list(),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook for fetching diagnoses (for product targeting)
 */
export function useDiagnoses() {
  return useQuery({
    queryKey: vendorProductKeys.diagnoses(),
    queryFn: () => diagnosesApi.list(),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook for product form data (categories + diagnoses)
 */
export function useProductFormData() {
  const categoriesQuery = useVendorCategories();
  const diagnosesQuery = useDiagnoses();

  return {
    categories: categoriesQuery.data ?? [],
    diagnoses: diagnosesQuery.data ?? [],
    isLoading: categoriesQuery.isLoading || diagnosesQuery.isLoading,
    isError: categoriesQuery.isError || diagnosesQuery.isError,
    error: categoriesQuery.error || diagnosesQuery.error,
  };
}

